import { supabase } from '@/integrations/supabase/client';

/**
 * SAB-977: after a failed password sign-in, say WHICH provider owns the account.
 *
 * A user who signed up with Google and then types a password gets "Invalid login
 * credentials", which is true and useless: there is no password to be wrong. This asks the
 * server, and the server answers only for accounts that genuinely cannot use a password.
 *
 * ── CALLED ONLY AFTER A FAILURE ─────────────────────────────────────────────
 *
 * Never on page load, never as the user types. The endpoint is rate limited server-side
 * regardless, but calling it speculatively would burn a real user's budget and turn a
 * login screen into a background prober.
 *
 * ── FAILS SILENT, ALWAYS ────────────────────────────────────────────────────
 *
 * Any error, any unexpected shape, an old client, a missing RPC: return null and let the
 * caller show the ordinary invalid-credentials message. A hint that throws would replace a
 * confusing login error with a broken one.
 *
 * The server returns an empty list for an unknown address, for an account that can hold a
 * password, and when rate limited -- three states it deliberately cannot tell apart. So an
 * empty answer here means "say nothing extra", not "no such account".
 */
const LABELS: Record<string, string> = { google: 'Google', apple: 'Apple' };

export async function fetchProviderHint(email: string): Promise<string | null> {
  const trimmed = email.trim();
  if (!trimmed) return null;
  try {
    const { data, error } = await supabase.rpc('auth_provider_hint' as never, {
      p_email: trimmed,
    } as never);
    if (error) return null;
    const providers = (data as { providers?: unknown } | null)?.providers;
    if (!Array.isArray(providers) || providers.length === 0) return null;
    const first = providers[0];
    if (typeof first !== 'string') return null;
    // Only render names we recognise. An unexpected value is a server-side change we have
    // not seen; showing it raw would put an unreviewed string in front of the user.
    return LABELS[first] ?? null;
  } catch {
    return null;
  }
}

/** The message shown in place of "Invalid login credentials". */
export function providerHintMessage(label: string): string {
  return `This account signs in with ${label}. Use Forgot password to set one.`;
}
